import { useRef, useState, type DragEvent as ReactDragEvent } from 'react';
import { workspaceViewportOptions } from './workspaceViewport';

import { Icon } from '../../components/Icon';
import { downloadDataExportFile } from '../../data/export/browserFile';
import { prepareTableCsvExport } from '../../data/export/prepare';
import { isTableSource, isVectorSource, type SourceId } from '../../data/model';
import type { LayerDefinition, LayerId } from '../../project/model';
import type { ApplicationMode } from '../modes/applicationModes';
import { useAppStore } from '../state/useAppStore';
import { useCapability } from '../../features/useCapability';
import { ExportDataDialog } from './data/ExportDataDialog';
import { ReplaceLayerDataDialog } from './data/ReplaceLayerDataDialog';
import { LayerRow, type LayerDropPosition } from './LayerRow';
import { TableRow } from './TableRow';
import { AnnotationLayerRow } from './AnnotationLayerRow';

interface LayersPanelProps {
  readonly mode: Exclude<ApplicationMode, 'empty'>;
}

function tableRemovalUsage(layers: readonly LayerDefinition[], tableId: SourceId) {
  const affectedLayers = layers.filter((layer) =>
    (layer.joins ?? []).some((join) => join.tableSourceId === tableId),
  );
  const joinCount = affectedLayers.reduce(
    (count, layer) =>
      count + (layer.joins ?? []).filter((join) => join.tableSourceId === tableId).length,
    0,
  );
  return joinCount > 0
    ? { joinCount, layerNames: affectedLayers.map((layer) => layer.name) }
    : undefined;
}

export function LayersPanel({ mode }: LayersPanelProps) {
  const panelRef = useRef<HTMLElement>(null);
  const canExportData = useCapability('data.export');
  const canAuthorAnnotations = useCapability('annotations.author');
  const canReplaceData = useCapability('data.refresh');
  const [exportLayerId, setExportLayerId] = useState<LayerId | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<{
    readonly layerId: LayerId;
    readonly returnFocusTo: HTMLElement | null;
  } | null>(null);
  const [draggedLayerId, setDraggedLayerId] = useState<LayerId | null>(null);
  const [dropTarget, setDropTarget] = useState<{
    readonly layerId: LayerId;
    readonly position: Exclude<LayerDropPosition, null>;
  } | null>(null);
  const project = useAppStore((state) => state.project);
  const selectedLayerId = useAppStore((state) => state.shell.selectedLayerId);
  const selectedTableSourceId = useAppStore((state) => state.shell.selectedTableSourceId);
  const expandedLayerIds = useAppStore((state) => state.shell.expandedLayerIds);
  const collapsed = useAppStore((state) => state.shell.layersPanelCollapsed);
  const removeLayer = useAppStore((state) => state.removeLayer);
  const renameLayer = useAppStore((state) => state.renameLayer);
  const removeTableSource = useAppStore((state) => state.removeTableSource);
  const reorderLayer = useAppStore((state) => state.reorderLayer);
  const selectLayer = useAppStore((state) => state.selectLayer);
  const toggleLayerExpanded = useAppStore((state) => state.toggleLayerExpanded);
  const toggleLayerVisibility = useAppStore((state) => state.toggleLayerVisibility);
  const toggleLayersPanel = useAppStore((state) => state.toggleLayersPanel);
  const viewLayerData = useAppStore((state) => state.viewLayerData);
  const viewTableData = useAppStore((state) => state.viewTableData);
  const zoomToLayer = useAppStore((state) => state.zoomToLayer);
  const addAnnotationLayer = useAppStore((state) => state.addAnnotationLayer);
  const removeAnnotationLayer = useAppStore((state) => state.removeAnnotationLayer);
  const renameAnnotationLayer = useAppStore((state) => state.renameAnnotationLayer);
  const reorderAnnotationLayer = useAppStore((state) => state.reorderAnnotationLayer);
  const selectAnnotationLayer = useAppStore((state) => state.selectAnnotationLayer);
  const toggleAnnotationLayerVisibility = useAppStore(
    (state) => state.toggleAnnotationLayerVisibility,
  );
  const activeAnnotationLayerId = useAppStore((state) => state.shell.activeAnnotationLayerId);
  const sourcesById = new Map(project.sources.map((source) => [source.id, source]));
  const layers = project.layers;
  const annotationLayers = project.annotationLayers;
  const tables = project.sources.filter(isTableSource);
  const selectCompositionElement = useAppStore((state) => state.selectCompositionElement);
  const removeInset = useAppStore((state) => state.updateCompositionInsetMap);
  const panelTitle = mode === 'composition' ? 'Page contents' : 'Layers';
  const activeDraggedLayerId =
    mode === 'editor' && layers.some((layer) => layer.id === draggedLayerId)
      ? draggedLayerId
      : null;
  const replacementLayer = replaceTarget
    ? project.layers.find((layer) => layer.id === replaceTarget.layerId)
    : undefined;
  const replacementSource = replacementLayer
    ? project.sources.find((source) => source.id === replacementLayer.sourceId)
    : undefined;

  const clearDragState = () => {
    setDraggedLayerId(null);
    setDropTarget(null);
  };

  const handleDrop = (
    event: ReactDragEvent<HTMLLIElement>,
    targetLayerId: LayerId,
    targetIndex: number,
  ) => {
    event.preventDefault();
    if (!activeDraggedLayerId || !dropTarget || dropTarget.layerId !== targetLayerId) {
      clearDragState();
      return;
    }
    const currentIndex = layers.findIndex((layer) => layer.id === activeDraggedLayerId);
    if (currentIndex < 0) {
      clearDragState();
      return;
    }
    const insertionIndex = targetIndex + (dropTarget.position === 'after' ? 1 : 0);
    const finalIndex = insertionIndex > currentIndex ? insertionIndex - 1 : insertionIndex;
    reorderLayer(activeDraggedLayerId, finalIndex);
    clearDragState();
  };

  return (
    <aside className={`layers-panel${collapsed ? ' layers-panel-collapsed' : ''}`} ref={panelRef}>
      <div className="panel-heading">
        {!collapsed ? (
          <div className="panel-title">
            <Icon name={mode === 'composition' ? 'map' : 'layers'} />
            <h2>{panelTitle}</h2>
          </div>
        ) : null}
        <button
          aria-label={collapsed ? 'Expand Layers panel' : 'Collapse Layers panel'}
          className="icon-button panel-collapse-button"
          onClick={toggleLayersPanel}
          title={collapsed ? 'Expand panel' : 'Collapse panel'}
          type="button"
        >
          <Icon name={collapsed ? 'expand' : 'collapse'} />
        </button>
      </div>

      {!collapsed ? (
        <div className="panel-body">
          {mode === 'composition' ? (
            <div className="page-content-row">
              <span className="page-content-preview" aria-hidden="true" />
              <span>Live map frame</span>
            </div>
          ) : null}

          {mode === 'composition' && project.composition.insetMap ? (
            <div className="page-content-row">
              <button
                type="button"
                className="secondary-button"
                onClick={() => selectCompositionElement('inset-map')}
              >
                Inset map
              </button>
              <button
                type="button"
                className="icon-button"
                aria-label="Remove inset map from page"
                onClick={(event) => {
                  const fallback = event.currentTarget
                    .closest('aside')
                    ?.querySelector<HTMLButtonElement>('.panel-collapse-button');
                  removeInset(null);
                  requestAnimationFrame(() => fallback?.focus());
                }}
              >
                <Icon name="trash" />
              </button>
            </div>
          ) : null}
          <div className="panel-section-heading">
            <span>{mode === 'composition' ? 'Map layers' : 'Layers'}</span>
            <span>{layers.length + annotationLayers.length}</span>
          </div>
          <ul aria-label="Map layers" className="layer-list">
            {annotationLayers.map((layer, index) => (
              <AnnotationLayerRow
                authoring={canAuthorAnnotations && (mode === 'editor' || mode === 'composition')}
                first={index === 0}
                key={layer.id}
                last={index === annotationLayers.length - 1}
                layer={layer}
                onMove={(direction) => reorderAnnotationLayer(layer.id, index + direction)}
                onRemove={() => removeAnnotationLayer(layer.id)}
                onRename={(name) => renameAnnotationLayer(layer.id, name).ok}
                onSelect={() => selectAnnotationLayer(layer.id)}
                onToggleVisibility={() => toggleAnnotationLayerVisibility(layer.id)}
                selected={
                  (mode === 'editor' || mode === 'composition') &&
                  selectedLayerId === null &&
                  activeAnnotationLayerId === layer.id
                }
              />
            ))}
            {layers.map((layer, index) => {
              const source = sourcesById.get(layer.sourceId);
              if (!source || !isVectorSource(source)) return null;
              const reorderable = mode === 'editor';
              return (
                <LayerRow
                  dropPosition={
                    activeDraggedLayerId && dropTarget?.layerId === layer.id
                      ? dropTarget.position
                      : null
                  }
                  expanded={expandedLayerIds.includes(layer.id)}
                  exportAvailable={mode !== 'composition' && canExportData}
                  first={index === 0}
                  key={layer.id}
                  last={index === layers.length - 1}
                  layer={layer}
                  onDragEnd={clearDragState}
                  onDragOver={(event) => {
                    if (!activeDraggedLayerId) return;
                    event.preventDefault();
                    event.dataTransfer.dropEffect = 'move';
                    const row = event.currentTarget.querySelector('.layer-row');
                    const bounds = row?.getBoundingClientRect();
                    const position =
                      bounds && event.clientY >= bounds.top + bounds.height / 2
                        ? 'after'
                        : 'before';
                    setDropTarget({ layerId: layer.id, position });
                  }}
                  onDragStart={(event) => {
                    event.dataTransfer.effectAllowed = 'move';
                    event.dataTransfer.setData('text/plain', layer.id);
                    setDraggedLayerId(layer.id);
                    setDropTarget(null);
                  }}
                  onDrop={(event) => handleDrop(event, layer.id, index)}
                  onExport={() => setExportLayerId(layer.id)}
                  onMoveDown={() => reorderLayer(layer.id, index + 1)}
                  onMoveUp={() => reorderLayer(layer.id, index - 1)}
                  onRemove={() => removeLayer(layer.id)}
                  onReplace={(returnFocusTo) =>
                    setReplaceTarget({ layerId: layer.id, returnFocusTo })
                  }
                  onRename={(name) => renameLayer(layer.id, name).ok}
                  onSelect={() => selectLayer(layer.id)}
                  onToggleExpanded={() => toggleLayerExpanded(layer.id)}
                  onToggleVisibility={() => toggleLayerVisibility(layer.id)}
                  onViewData={() => viewLayerData(layer.id)}
                  onZoom={() => zoomToLayer(layer.id, workspaceViewportOptions(panelRef.current))}
                  removable={mode === 'editor'}
                  reorderable={reorderable}
                  selectable={mode !== 'composition'}
                  selected={mode !== 'composition' && selectedLayerId === layer.id}
                  source={source}
                  symbolsExpandable={mode === 'editor'}
                  viewDataAvailable={mode !== 'composition' && source.fields.length > 0}
                  readOnly={mode === 'composition'}
                  renameAvailable={mode === 'editor' || mode === 'composition'}
                  replacementAvailable={canReplaceData}
                  replacementVisible={mode === 'editor'}
                />
              );
            })}
          </ul>

          {mode === 'composition' ? (
            <button
              className="annotation-layer-add"
              disabled={!canAuthorAnnotations}
              onClick={() => addAnnotationLayer()}
              title={
                canAuthorAnnotations
                  ? 'Create a geographic presentation layer'
                  : 'Annotation authoring requires Pro'
              }
              type="button"
            >
              <Icon name={canAuthorAnnotations ? 'plus' : 'lock'} /> Annotation layer
            </button>
          ) : null}

          {mode !== 'composition' && tables.length > 0 ? (
            <section className="tables-section" aria-labelledby="tables-heading">
              <div className="panel-section-heading" id="tables-heading">
                <span>Tables</span>
                <span>{tables.length}</span>
              </div>
              <ul aria-label="Tables" className="table-list">
                {tables.map((table) => (
                  <TableRow
                    exportAvailable={canExportData}
                    key={table.id}
                    onExport={async () => {
                      downloadDataExportFile(await prepareTableCsvExport(table));
                    }}
                    onRemove={() => removeTableSource(table.id)}
                    onViewData={() => viewTableData(table.id)}
                    removable={mode === 'editor'}
                    removalUsage={tableRemovalUsage(layers, table.id)}
                    selected={selectedTableSourceId === table.id}
                    source={table}
                  />
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      ) : null}
      {mode === 'editor' && !collapsed ? (
        <div className="layers-bottom-actions">
          <div data-import-actions="panel" />
          <button
            className="annotation-layer-add"
            disabled={!canAuthorAnnotations}
            onClick={() => addAnnotationLayer()}
            title={
              canAuthorAnnotations
                ? 'Create a geographic presentation layer'
                : 'Annotation authoring requires Pro'
            }
            type="button"
          >
            <Icon name="plus" /> Annotation layer
          </button>
        </div>
      ) : null}
      {mode !== 'composition' && exportLayerId ? (
        <ExportDataDialog layerId={exportLayerId} onClose={() => setExportLayerId(null)} />
      ) : null}
      {mode === 'editor' &&
      replaceTarget &&
      replacementLayer &&
      replacementSource &&
      isVectorSource(replacementSource) ? (
        <ReplaceLayerDataDialog
          layer={replacementLayer}
          onClose={() => setReplaceTarget(null)}
          returnFocusTo={replaceTarget.returnFocusTo}
          source={replacementSource}
        />
      ) : null}
    </aside>
  );
}
